import { useEffect, useRef } from 'react';
import { Typography } from '@material-ui/core';
import { useStyles } from '../../../styles';
import { ExecutionEvent } from '../../../interfaces';
import { ThreeDotIndicator } from '../../../common/ThreeDotIndicator';

interface WorkLogProps {
  isGenerating?: boolean;
  events: ExecutionEvent[];
  searchText?: string;
}

export function WorkLog({
  isGenerating = false,
  events: executionLogs,
  searchText = '',
}: WorkLogProps) {
  const classes = useStyles();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [executionLogs]);

  const filteredEntries = executionLogs.filter(entry => {
    if (!entry.event || !entry.timestamp) {
      return false;
    }
    const normalizedSearchText = searchText.trim().toLowerCase();
    if (!normalizedSearchText) {
      return true;
    }
    const displayText = entry.message;
    return displayText?.toLowerCase().includes(normalizedSearchText);
  });

  const orderedEntries = [...filteredEntries].reverse();

  return (
    <div className={`rounded-lg shadow-sm ${classes.workLogContainer}`}>
      {filteredEntries.length > 0 && isGenerating && (
        <div className="flex flex-col items-center justify-center py-4 space-y-4">
          <ThreeDotIndicator />
          <Typography variant="body2" className={classes.indexReportInfoText}>
            Agent is processing your request... Please wait
          </Typography>
        </div>
      )}
      <div
        ref={scrollContainerRef}
        className={`overflow-y-auto ${classes.workLogScrollContainer}`}
      >
        <div className="min-w-full">
          {orderedEntries.map((entry, index) => {
            const timePart = entry.timestamp?.includes('T')
              ? entry.timestamp.split('T')[1].split('.')[0]
              : entry.timestamp ?? '';
            return (
              <div
                key={index}
                className={`${classes.workLogEntry} flex items-center px-4 py-3 transition-colors ${classes.workLogRow}`}
              >
                <div className="flex-shrink-0 w-20">
                  <span
                    className={`text-sm font-mono ${classes.workLogEntryId}`}
                  >
                    {timePart}
                  </span>
                </div>
                <div className="flex-1 ml-4">
                  <span
                    className={`text-sm font-mono ${classes.workLogEntryDescription}`}
                  >
                    {entry.message || entry.event}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
